// frontend/src/modules/monitoring/pages/AgentConnectPage.tsx
//
// One-visit agent setup handoff. A monitored employee who hasn't connected
// the desktop app yet lands here (from an onboarding email, a nav link, or
// the AgentSetupCard) and — with no clicks required — we issue a fresh
// setup token and fire the worqly-agent:// deep link immediately, so the
// desktop app opens pre-filled and ready to save. Every other UI element
// (download buttons, manual token, "Check connection") is a fallback for
// when the auto-open is blocked or the app isn't installed yet.
//
// Reachable at /dashboard/agent/connect (and the bare /agent/connect, which
// redirects there — see App.tsx).

import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MonitorDown, CheckCircle2, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { VISUAL } from '../../../design-system/visual';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { monitoringApi, type SelfAgentSetup } from '../api';
import { agentReleasesApi, type AgentRelease } from '../../agent-releases/api';
import { buildDeepLink, navigateToDeepLink } from '../deepLink';
import { ApiError } from '../../../core/api-client';

const AUTO_OPEN_DELAY_MS = 500;

export function AgentConnectPage() {
  const [setup, setSetup] = React.useState<SelfAgentSetup | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [release, setRelease] = React.useState<AgentRelease | null>(null);
  const [plaintext, setPlaintext] = React.useState<string | null>(null);
  const [checking, setChecking] = React.useState(false);
  const issuedRef = React.useRef(false);

  const load = React.useCallback(() => {
    monitoringApi.getAgentSetup()
      .then(setSetup)
      .catch((err) => {
        if (!(err instanceof ApiError)) console.warn('[agent-connect] fetch failed', err);
        setSetup(null);
      })
      .finally(() => setLoaded(true));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const needsSetup = loaded && !!setup?.monitored && !setup.connected;

  // Downloads, for the "not installed" fallback.
  React.useEffect(() => {
    if (needsSetup) {
      agentReleasesApi.getTenant().then(setRelease).catch(() => undefined);
    }
  }, [needsSetup]);

  // Auto-issue a token and auto-fire the deep link, exactly once (StrictMode
  // double-invokes effects in dev, hence the ref guard).
  React.useEffect(() => {
    if (!needsSetup || issuedRef.current) return;
    issuedRef.current = true;

    monitoringApi.issueSelfToken()
      .then((r) => {
        setPlaintext(r.plaintext);
        const deepLink = buildDeepLink(r.plaintext);
        window.setTimeout(() => navigateToDeepLink(deepLink), AUTO_OPEN_DELAY_MS);
      })
      .catch((err: any) => {
        toast.error('Could not prepare your setup link', { description: err?.message });
      });
  }, [needsSetup]);

  async function checkConnection() {
    setChecking(true);
    try {
      const s = await monitoringApi.getAgentSetup();
      setSetup(s);
      if (s.connected) toast.success('Desktop app connected');
      else toast.info('Not connected yet', { description: 'Finish setup in the app — it checks in within ~20 seconds.' });
    } catch { /* keep prior state */ }
    finally { setChecking(false); }
  }

  async function copyToken() {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      toast.success('Token copied');
    } catch { toast.error('Could not copy — select and copy manually'); }
  }

  if (!loaded) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-16">
        <LoadingState variant="page" label="Checking your account" />
      </div>
    );
  }

  if (!setup?.monitored) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-16 max-w-lg mx-auto">
        <section className={[VISUAL.bento, 'p-6 text-center'].join(' ')}>
          <MonitorDown className="h-8 w-8 text-muted-foreground mx-auto" />
          <h1 className="mt-4 text-[16px] font-bold text-foreground">Monitoring isn't enabled for your account</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">
            There's nothing to connect here right now. If you think this is a mistake, check with your
            workspace admin.
          </p>
          <Link
            to="/dashboard"
            className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-saffron-700 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to dashboard
          </Link>
        </section>
      </div>
    );
  }

  if (setup.connected) {
    return (
      <div className="px-6 sm:px-8 lg:px-10 py-16 max-w-lg mx-auto">
        <section className={[VISUAL.bento, 'p-6 text-center'].join(' ')}>
          <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
          <h1 className="mt-4 text-[16px] font-bold text-foreground">Desktop app already connected</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">
            You're all set{setup.last_seen_version ? ` — running version ${setup.last_seen_version}` : ''}.
            Clock in and out from the app.
          </p>
          <Link
            to="/dashboard"
            className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-saffron-700 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to dashboard
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="px-6 sm:px-8 lg:px-10 py-16 max-w-lg mx-auto">
      <section className={[VISUAL.bento, 'p-6 sm:p-8 text-center'].join(' ')}>
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 mx-auto">
          <MonitorDown className="h-6 w-6 text-saffron-700" />
        </div>
        <h1 className="mt-4 text-[17px] font-bold text-foreground">Opening the Worqly desktop app…</h1>
        <p className="mt-2 text-[13px] text-muted-foreground">
          Your workspace tracks work sessions through the desktop app. We're opening it now with your
          setup details filled in — if nothing happens, use the button below.
        </p>

        <div className="mt-5">
          <Button
            size="lg"
            className="rounded-full w-full sm:w-auto"
            disabled={!plaintext}
            onClick={() => plaintext && navigateToDeepLink(buildDeepLink(plaintext))}
          >
            {plaintext ? 'Open the Worqly app' : 'Preparing…'}
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
          {release?.windows_url && (
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <a href={release.windows_url}>Download for Windows</a>
            </Button>
          )}
          {release?.macos_url && (
            <Button asChild variant="outline" size="sm" className="rounded-full">
              <a href={release.macos_url}>Download for macOS</a>
            </Button>
          )}
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={checkConnection} disabled={checking}>
            <RefreshCw className={checking ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            Check connection
          </Button>
        </div>

        {plaintext && (
          <details className="mt-5 text-left border-t border-border/40 pt-4">
            <summary className="text-[12px] text-muted-foreground cursor-pointer text-center">Manual setup (paste the token in the app)</summary>
            <div className="flex items-center gap-2 mt-2">
              <Input readOnly value={plaintext} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs h-8" />
              <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={copyToken}>Copy</Button>
            </div>
          </details>
        )}

        <Link
          to="/dashboard"
          className="mt-6 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to dashboard
        </Link>
      </section>
    </div>
  );
}
